import React, { useState } from 'react';
import {
  Text,
  TabList,
  Tab,
  Button,
  Label,
  Select,
  Checkbox,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ChevronUp20Regular, ChevronDown20Regular } from '@fluentui/react-icons';
import type { DataModelView } from '../../api/dataModelsService';
import type { JsonFieldPromotionOverrides } from '../../api/dataModelsService';
import type { PhysicalTable, SelectionType } from './types';

const useStyles = makeStyles({
  container: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    display: 'flex',
    flexDirection: 'column',
    transition: 'height 0.2s',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 16px',
    minHeight: '36px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  summaryText: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '12px 16px',
  },
  sqlBlock: {
    fontFamily: 'monospace',
    fontSize: '12px',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: '12px',
    borderRadius: '6px',
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    maxHeight: '180px',
    overflow: 'auto',
  },
  empty: {
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
    padding: '20px',
  },
  lineageGroup: {
    marginBottom: '12px',
  },
  lineageStep: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '3px 0',
  },
  lineageArrow: {
    color: tokens.colorNeutralForeground3,
    paddingLeft: '16px',
  },
  classification: {
    display: 'grid',
    gridTemplateColumns: 'max-content minmax(180px, 320px)',
    gap: '8px 16px',
    alignItems: 'center',
  },
  error: {
    gridColumn: '1 / -1',
    color: tokens.colorPaletteRedForeground1,
  },
  promotions: { gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: '4px' },
});

interface BottomDrawerProps {
  selection: SelectionType;
  views: DataModelView[];
  tables: PhysicalTable[];
  isOpen: boolean;
  onToggle: () => void;
  onPrimaryTimeColumnOverride: (datasetId: string, override: string | null) => Promise<void>;
  onJsonFieldPromotionOverrides: (datasetId: string, overrides: JsonFieldPromotionOverrides) => Promise<void>;
  isReadOnly: boolean;
}

const BottomDrawer: React.FC<BottomDrawerProps> = ({
  selection,
  views,
  tables,
  isOpen,
  onToggle,
  onPrimaryTimeColumnOverride,
  onJsonFieldPromotionOverrides,
  isReadOnly,
}) => {
  const styles = useStyles();
  const [activeTab, setActiveTab] = useState<string>('sql');
  const [isSavingPrimaryTime, setIsSavingPrimaryTime] = useState(false);
  const [primaryTimeError, setPrimaryTimeError] = useState<string | null>(null);
  const [promotionError, setPromotionError] = useState<string | null>(null);
  const [isSavingPromotions, setIsSavingPromotions] = useState(false);

  const selectedView = selection?.kind === 'view'
    ? views.find((v) => v.id === selection.id) ?? null
    : null;

  const selectedTable = selection?.kind === 'table'
    ? tables.find((t) => t.id === selection.id) ?? null
    : null;

  const drawerHeight = isOpen ? 220 : 36;

  const temporalColumns = selectedTable?.columns.filter((column) =>
    /(date|time|timestamp|datetime)/i.test(column.type)
    || /(date|_dt$|_time$|_at$|timestamp|datetime|day$)/i.test(column.name),
  ) ?? [];

  const updatePrimaryTimeColumn = async (value: string) => {
    if (!selectedTable || isReadOnly) return;
    setIsSavingPrimaryTime(true);
    setPrimaryTimeError(null);
    try {
      await onPrimaryTimeColumnOverride(selectedTable.datasetId, value || null);
    } catch {
      setPrimaryTimeError('Could not update the primary time column or start the rebuild.');
    } finally {
      setIsSavingPrimaryTime(false);
    }
  };

  const promotionKey = (sourceColumn: string, path: string) => `${sourceColumn}\u0000${path}`;
  const recommendedKeys = new Set((selectedTable?.jsonFieldPromotionRecommendations ?? [])
    .map(item => promotionKey(item.source_column, item.path)));
  const overrideIncludes = new Set((selectedTable?.jsonFieldPromotionOverrides?.include ?? [])
    .map(item => promotionKey(item.sourceColumn, item.path)));
  const overrideExcludes = new Set((selectedTable?.jsonFieldPromotionOverrides?.exclude ?? [])
    .map(item => promotionKey(item.sourceColumn, item.path)));
  const updatePromotion = async (sourceColumn: string, path: string, checked: boolean) => {
    if (!selectedTable || isReadOnly) return;
    const key = promotionKey(sourceColumn, path);
    const include = new Map((selectedTable.jsonFieldPromotionOverrides?.include ?? [])
      .map(item => [promotionKey(item.sourceColumn, item.path), item]));
    const exclude = new Map((selectedTable.jsonFieldPromotionOverrides?.exclude ?? [])
      .map(item => [promotionKey(item.sourceColumn, item.path), item]));
    if (checked) {
      exclude.delete(key);
      if (!recommendedKeys.has(key)) include.set(key, { sourceColumn, path });
    } else {
      include.delete(key);
      if (recommendedKeys.has(key)) exclude.set(key, { sourceColumn, path });
    }
    setIsSavingPromotions(true);
    setPromotionError(null);
    try {
      await onJsonFieldPromotionOverrides(selectedTable.datasetId, {
        version: 1, include: [...include.values()], exclude: [...exclude.values()],
      });
    } catch {
      setPromotionError('Could not update JSON-field promotions or start the rebuild.');
    } finally {
      setIsSavingPromotions(false);
    }
  };

  // Summary text when collapsed
  const summaryLabel = selectedView
    ? `${selectedView.title || selectedView.name} · SQL available`
    : selectedTable
      ? `${selectedTable.aliasName || selectedTable.displayName || selectedTable.name} selected`
      : '';

  // Resolve lineage for multi-fact views
  const renderLineage = () => {
    if (!selectedView) {
      return (
        <div className={styles.empty}>
          <Text size={200}>Select a semantic view to see lineage.</Text>
        </div>
      );
    }

    const isMultiFact = selectedView.viewType === 'multi_fact';

    if (isMultiFact) {
      // Try to resolve child fact views
      const childViews = selectedView.baseTables
        .map((name) => views.find((v) => v.name.toLowerCase() === name.toLowerCase()))
        .filter(Boolean) as DataModelView[];

      if (childViews.length > 0) {
        return (
          <div>
            {childViews.map((cv) => (
              <div key={cv.id} className={styles.lineageGroup}>
                {cv.baseTables.map((t, i) => (
                  <div key={i} className={styles.lineageStep}>
                    <Text size={200}>📄 {t}</Text>
                  </div>
                ))}
                <div className={styles.lineageArrow}>
                  <Text size={200}>→ 🔷 {cv.title || cv.name}</Text>
                </div>
              </div>
            ))}
            <div className={styles.lineageArrow}>
              <Text size={200} weight="semibold">→ 🔶 {selectedView.title || selectedView.name}</Text>
            </div>
          </div>
        );
      }
    }

    // Standard lineage for fact/dimension
    if (selectedView.baseTables.length === 0) {
      return (
        <div className={styles.empty}>
          <Text size={200}>Source tables could not be resolved for this semantic view.</Text>
        </div>
      );
    }

    return (
      <div>
        {selectedView.baseTables.map((t, i) => (
          <div key={i} className={styles.lineageStep}>
            <Text size={200}>📄 {t}</Text>
          </div>
        ))}
        <div className={styles.lineageArrow}>
          <Text size={200} weight="semibold">→ 🔷 {selectedView.title || selectedView.name}</Text>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container} style={{ height: drawerHeight }}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <TabList
            selectedValue={activeTab}
            onTabSelect={(_, data) => setActiveTab(data.value as string)}
            size="small"
          >
            <Tab value="sql">SQL</Tab>
            <Tab value="preview">Preview</Tab>
            <Tab value="lineage">Lineage</Tab>
            <Tab value="diagnostics">Diagnostics</Tab>
          </TabList>
          {!isOpen && summaryLabel && (
            <span className={styles.summaryText}>{summaryLabel}</span>
          )}
        </div>
        <Button
          appearance="subtle"
          icon={isOpen ? <ChevronDown20Regular /> : <ChevronUp20Regular />}
          size="small"
          onClick={onToggle}
        />
      </div>

      {isOpen && (
        <div className={styles.content}>
          {activeTab === 'sql' && (
            <>
              {selectedView ? (
                <pre className={styles.sqlBlock}>{selectedView.sqlDefinition}</pre>
              ) : selectedTable ? (
                <div className={styles.empty}>
                  <Text size={200}>No SQL for physical table.</Text>
                </div>
              ) : (
                <div className={styles.empty}>
                  <Text size={200}>Select a table or semantic view to inspect details.</Text>
                </div>
              )}
            </>
          )}

          {activeTab === 'preview' && (
            <div className={styles.empty}>
              <Text size={200}>
                {selection ? 'Preview data not available in read-only mode.' : 'Select a table or semantic view to preview data.'}
              </Text>
            </div>
          )}

          {activeTab === 'lineage' && renderLineage()}

          {activeTab === 'diagnostics' && (
            <>
              {selectedTable ? (
                <div className={styles.classification}>
                  <Label htmlFor="primary-time-column-override">Primary time column</Label>
                  <Select
                    id="primary-time-column-override"
                    value={selectedTable.primaryTimeColumnOverride ?? ''}
                    disabled={isReadOnly || isSavingPrimaryTime || temporalColumns.length === 0}
                    onChange={(event) => void updatePrimaryTimeColumn(event.target.value)}
                  >
                    <option value="">Automatic (detected)</option>
                    {temporalColumns.map((column) => (
                      <option key={column.name} value={column.name}>{column.name}</option>
                    ))}
                  </Select>
                  {(selectedTable.jsonFieldPromotionCandidates?.length ?? 0) > 0 && (
                    <div className={styles.promotions}>
                      <Text weight="semibold">Promoted JSON fields</Text>
                      {(selectedTable.jsonFieldPromotionCandidates ?? []).map(item => {
                        const key = promotionKey(item.source_column, item.path);
                        const checked = overrideIncludes.has(key)
                          || (recommendedKeys.has(key) && !overrideExcludes.has(key));
                        return <Checkbox
                          key={key}
                          checked={checked}
                          disabled={isReadOnly || isSavingPromotions}
                          label={`${item.source_column}${item.path.slice(1)} → ${item.alias} (${item.dtype})`}
                          onChange={(_, data) => void updatePromotion(item.source_column, item.path, data.checked === true)}
                        />;
                      })}
                    </div>
                  )}
                  {primaryTimeError && <Text size={200} className={styles.error}>{primaryTimeError}</Text>}
                  {promotionError && <Text size={200} className={styles.error}>{promotionError}</Text>}
                </div>
              ) : selectedView?.validationError ? (
                <div>
                  <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>
                    ⚠️ {selectedView.validationError}
                  </Text>
                </div>
              ) : (
                <div className={styles.empty}>
                  <Text size={200}>No diagnostics issues detected.</Text>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BottomDrawer;